import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Plus, Search, ArrowRight, FileDown, PartyPopper, Truck } from "lucide-react";

export const eur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
export const fmtDate = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
export const STATI = {
  bozza: ["Bozza", "bg-slate-100 text-slate-700 border-slate-300"],
  inviato: ["Inviato", "bg-sky-50 text-sky-700 border-sky-200"],
  accettato: ["Accettato", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  rifiutato: ["Rifiutato", "bg-red-50 text-red-700 border-red-200"],
};
export const StatoBadge = ({ stato }) => {
  const [label, cls] = STATI[stato] || STATI.bozza;
  return <span data-testid={`stato-badge-${stato}`} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
};

export const downloadPdf = async (id, codice, evento = false) => {
  const res = await api.get(`/amministrazione/${evento ? "preventivi-eventi" : "preventivi"}/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a"); a.href = url; a.download = `${codice}.pdf`; a.click();
  URL.revokeObjectURL(url);
};

export default function PreventiviPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tipo = params.get("tipo") === "evento" ? "evento" : "trasporto";
  const isEv = tipo === "evento";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("all");
  useEffect(() => {
    setItems(null);
    api.get(isEv ? "/amministrazione/preventivi-eventi" : "/amministrazione/preventivi").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [isEv]);
  const list = (items || []).filter((p) => (stato === "all" || (p.stato || "bozza") === stato) && (!q || (p.cliente?.nominativo || "").toLowerCase().includes(q.toLowerCase()) || (p.codice || "").toLowerCase().includes(q.toLowerCase()) || (p.denominazione || "").toLowerCase().includes(q.toLowerCase())));
  const base = isEv ? "/operativa/amministrazione/preventivi-eventi" : "/operativa/amministrazione/preventivi";
  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto nx-fade-in pb-10">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2">{isEv ? <PartyPopper className="h-7 w-7 text-primary" /> : <FileText className="h-7 w-7 text-primary" />} Preventivi {isEv ? "Eventi" : "Trasporto"}</h1>
            <p className="text-sm text-slate-500">{isEv ? "Preventivi per assistenza sanitaria a eventi e manifestazioni (tariffe orarie)." : "Preventivi trasporto sanitario secondo Delibera N. XI/6645/2017."}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="preventivi-tipo-switch">
              <button type="button" data-testid="preventivi-tipo-trasporto" onClick={() => setParams({})}
                className={`px-3 py-1.5 text-xs font-semibold rounded flex items-center gap-1.5 ${!isEv ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}><Truck className="h-3.5 w-3.5" /> Trasporto</button>
              <button type="button" data-testid="preventivi-tipo-evento" onClick={() => setParams({ tipo: "evento" })}
                className={`px-3 py-1.5 text-xs font-semibold rounded flex items-center gap-1.5 ${isEv ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}><PartyPopper className="h-3.5 w-3.5" /> Evento</button>
            </div>
            <Button data-testid="preventivo-new" className="gap-2" onClick={() => navigate(`${base}/nuovo`)}><Plus className="h-4 w-4" /> Nuovo preventivo {isEv ? "evento" : ""}</Button>
          </div>
        </div>
        <Card className="border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="preventivi-search" placeholder="Cerca cliente o numero..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
            </div>
            <Select value={stato} onValueChange={setStato}>
              <SelectTrigger data-testid="preventivi-filter-stato" className="h-9 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(STATI).map(([v, [l]]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="ml-auto text-xs text-slate-400">{items ? `${items.length} preventivi` : ""}</span>
          </div>
          {items === null ? <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-10 w-full" />)}</div>
            : list.length === 0 ? <div className="py-16 text-center text-sm text-slate-500">Nessun preventivo. Crea il primo preventivo.</div>
            : (
              <Table>
                <TableHeader><TableRow><TableHead className="w-40">Numero</TableHead><TableHead>{isEv ? "Richiedente" : "Cliente"}</TableHead><TableHead>Data</TableHead><TableHead>{isEv ? "Evento" : "Trasporto"}</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Importo totale</TableHead><TableHead className="w-24" /></TableRow></TableHeader>
                <TableBody>
                  {list.map((p) => (
                    <TableRow key={p.id} data-testid={`preventivo-row-${p.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`${base}/${p.id}`)}>
                      <TableCell className="font-mono text-xs font-semibold text-primary">{p.codice}</TableCell>
                      <TableCell className="font-semibold text-slate-800">{p.cliente?.nominativo || "—"}</TableCell>
                      <TableCell className="text-slate-600">{fmtDate(p.data)}</TableCell>
                      <TableCell className="text-slate-600">{isEv ? <>{p.denominazione || "—"} <span className="text-slate-400 text-xs">{fmtDate(p.inizio)}</span></> : fmtDate(p.data_trasporto)}</TableCell>
                      <TableCell><StatoBadge stato={p.stato} /></TableCell>
                      <TableCell className="text-right font-semibold" data-testid={`preventivo-totale-${p.id}`}>{eur(p.totale)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-primary" data-testid={`preventivo-pdf-${p.id}`} onClick={(e) => { e.stopPropagation(); downloadPdf(p.id, p.codice, isEv); }}><FileDown className="h-4 w-4" /></Button>
                        <ArrowRight className="h-4 w-4 text-slate-400 inline ml-1" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </Card>
      </div>
    </AppLayout>
  );
}
